import { useRef, useState, type KeyboardEvent } from "react";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** id of the draft input, so a sibling `<label htmlFor>` names it. Without
   *  it such a label points at nothing and the field has no accessible name. */
  id?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Optional max length per tag (chars). A longer tag is cut on commit
   *  with nothing said, so a caller sets it for values whose readability is
   *  the point (`labels:`, run tags). A `literalValues` list is exempt from
   *  it: a shortened literal is a different value, the same silent
   *  corruption as folding its case or splitting it on a comma. */
  maxTagLength?: number;
  /** Treat the values as LITERALS rather than labels: dedupe exactly
   *  (case included) and stop reading a comma as a separator.
   *
   *  The two defaults below exist because this widget was written for
   *  board labels. They are wrong for a value whose meaning is
   *  case-sensitive or that may contain a comma — a permission rule is
   *  both: the engine matches `Read(.env*)` and `Read(.ENV*)` as different
   *  rules, so folding them loses one silently. */
  literalValues?: boolean;
}

/** TagInput is a chip-based editor for string arrays. Used in the
 *  Board ticket form for labels, replacing the previous comma-separated
 *  text field. Commits a tag on Enter or comma; Backspace on an empty
 *  draft removes the last tag.
 *
 *  Tags are deduplicated case-insensitively but stored with the
 *  first-seen casing — unless `literalValues` is set.
 */
export function TagInput({
  value,
  onChange,
  id,
  placeholder = "Add tag…",
  disabled = false,
  maxTagLength = 64,
  literalValues = false,
}: Props) {
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  const commit = (raw: string) => {
    // A literal crosses whole; only a label is cut to length (maxTagLength).
    const trimmed = literalValues ? raw.trim() : raw.trim().slice(0, maxTagLength);
    if (!trimmed) return;
    const exists = value.some((t) =>
      literalValues ? t === trimmed : t.toLowerCase() === trimmed.toLowerCase(),
    );
    if (exists) {
      setDraft("");
      return;
    }
    onChange([...value, trimmed]);
    setDraft("");
  };

  const remove = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || (e.key === "," && !literalValues)) {
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      remove(value.length - 1);
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1 min-h-7 bg-surface-1 text-fg-default rounded-md border border-border-control px-1.5 py-1 focus-within:border-accent focus-within:ring-1 focus-within:ring-accent data-[disabled]:opacity-60 data-[disabled]:cursor-not-allowed`}
      data-disabled={disabled || undefined}
      onClick={() => inputRef.current?.focus()}
    >
      {value.map((tag, i) => (
        <span
          key={`${tag}-${i}`}
          className="inline-flex items-center gap-1 bg-surface-2 text-fg-default rounded-full px-2 py-0.5 text-micro"
        >
          {tag}
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled) remove(i);
            }}
            className="text-fg-subtle hover:text-fg-default leading-none"
          >
            ×
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        id={id}
        type="text"
        value={draft}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ""}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
        className="flex-1 min-w-[6rem] bg-transparent text-xs outline-none placeholder:text-fg-subtle"
      />
    </div>
  );
}
